import styles from './button.module.scss'
import { cn } from '../../lib/utils'
import { Slot } from '@radix-ui/react-slot'
import { VariantProps, cva } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import * as React from 'react'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-full text-base uppercase font-evogria ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-black hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border-2 border-primary bg-transparent',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-12 px-4 py-2',
        sm: 'h-9 px-3 text-sm',
        icon: 'h-10 w-10',
      },
    },
    compoundVariants: [
      {
        variant: 'outline',
        size: 'sm',
        class: 'border',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    if (asChild) {
      return (
        <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} disabled={disabled || isLoading} {...props}>
          {children}
        </Comp>
      )
    }

    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} disabled={disabled || isLoading} {...props}>
        {children}
        {isLoading && <Loader2 className="ml-2 h-4 w-4 animate-spin text-inherit" aria-hidden />}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export const PrimaryButton = ({ children, ...props }: ButtonProps): React.ReactElement => {
  return (
    <Button variant="default" {...props}>
      {children}
    </Button>
  )
}

export const SecondaryButton = ({ children, ...props }: ButtonProps): React.ReactElement => {
  return (
    <Button variant="secondary" {...props}>
      {children}
    </Button>
  )
}

export const OutlinedButton = ({ children, ...props }: ButtonProps): React.ReactElement => {
  return (
    <Button variant="outline" className={styles.outlined} {...props}>
      {children}
    </Button>
  )
}

export const LinkButton = ({ children, ...props }: ButtonProps): React.ReactElement => {
  return (
    <Button variant="link" {...props}>
      {children}
    </Button>
  )
}

export const IconButton = ({ children, ...props }: ButtonProps): React.ReactElement => {
  return (
    <Button variant="outline" size="icon" {...props}>
      {children}
    </Button>
  )
}

export { Button, buttonVariants }
